import React from "react";
import { Box, Typography } from "@mui/joy";
import SyncIcon from "@mui/icons-material/Sync";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PendingIcon from "@mui/icons-material/Pending";
import { Assignment } from "../../../api/assignments/Assignments.domain";
import { format } from "date-fns";
import { useRules } from "../../../contexts/controllers/useRules";
import { Rule } from "../../../api/rules/Rules.domain";
import { textStyles } from "../../../theme";

function getStatusIcon(status: string) {
  switch (status) {
    case "IN_PROGRESS":
      return <SyncIcon sx={{ color: "#FB8C00" }} />;
    case "EXECUTED":
      return <CheckCircleIcon sx={{ color: "#4CAF50" }} />;
    case "CANCELED":
      return <CancelIcon sx={{ color: "#F44336" }} />;
    default:
      return <PendingIcon />;
  }
}

// Helper to check whether an activity has a stop indicator.
function hasStopIndicator(
  activityTypeCode: string | undefined,
  returnStatus: string | undefined,
  rules: Rule[] | undefined,
): boolean {
  if (!activityTypeCode || !returnStatus || !rules || !rules.length) return false;

  try {
    const activityRule = rules.find((rule: Rule) => rule.activityTypeCode === activityTypeCode);

    if (!activityRule || !activityRule.possibleReturns) return false;

    const possibleReturn = activityRule.possibleReturns.find(
      (ret: any) => ret.returnStatus === returnStatus,
    );

    return Boolean(possibleReturn && "stop" in possibleReturn && possibleReturn.stop === true);
  } catch (error) {
    return false;
  }
}

interface AssignmentHeaderContentProps {
  assignment: Assignment;
  editMode: boolean;
}

const AssignmentHeaderContent: React.FC<AssignmentHeaderContentProps> = ({
  assignment,
  editMode,
}) => {
  const { rules } = useRules();

  const lastActivity = editMode ? assignment.activities?.[assignment.activities.length - 1] : null;
  const lastActivityLabel = editMode ? lastActivity?.activityType?.code || "N/A" : null;

  const activityHasStopIndicator = React.useMemo(() => {
    if (!lastActivity || !rules?.rules) return false;

    return hasStopIndicator(
      lastActivity.activityType?.code,
      lastActivity.returnStatus,
      rules.rules,
    );
  }, [lastActivity, rules]);

  return (
    <Box display="flex" alignItems="center" gap={1.5} sx={{ width: "100%" }}>
      {getStatusIcon(assignment.assignmentStatus)}
      <Box display="flex" flexDirection="column" sx={{ flexGrow: 1 }}>
        <Typography level="h4" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
          {assignment.externalAssignmentId}
          {assignment.adjustoId && (
            <Typography
              component="span"
              level="body-sm"
              color="neutral"
              sx={{ ml: 1, ...textStyles.metaInline }}
            >
              (AdjustoID: {assignment.adjustoId})
            </Typography>
          )}
          {!assignment.adjustoId && (
            <Typography
              component="span"
              level="body-sm"
              color="neutral"
              sx={{ ml: 1, ...textStyles.metaInline }}
            >
              (AdjustoID: N/A)
            </Typography>
          )}
        </Typography>
        <Box display="flex" alignItems="baseline" gap={1}>
          {assignment.claimId && (
            <Typography level="body-sm" color="neutral" sx={textStyles.meta}>
              (ClaimID: {assignment.claimId})
            </Typography>
          )}
          {assignment.benefitId && (
            <Typography level="body-sm" color="neutral" sx={textStyles.meta}>
              (BenefitID: {assignment.benefitId})
            </Typography>
          )}
          {assignment.servicePoint.logisticsMode?.code && (
            <Typography level="body-sm" color="neutral" sx={textStyles.meta}>
              {assignment.servicePoint.logisticsMode.code}
            </Typography>
          )}
        </Box>
      </Box>

      <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
        {editMode && (
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              level="body-sm"
              sx={{
                textAlign: "right",
                ...textStyles.badge,
              }}
            >
              Dernière activité: {lastActivityLabel}
            </Typography>
            {activityHasStopIndicator && (
              <Typography
                level="body-sm"
                sx={{
                  ...textStyles.badgeDanger,
                }}
              >
                ⚠️ Escalade SPB
              </Typography>
            )}
          </Box>
        )}

        <Typography
          level="body-sm"
          color="neutral"
          sx={{
            ...textStyles.meta,
            textAlign: "right",
          }}
        >
          Dernière MAJ: {format(new Date(assignment.lastUpdateDate), "dd/MM/yyyy HH:mm:ss")}
        </Typography>
      </Box>
    </Box>
  );
};

export default AssignmentHeaderContent;
